import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  CardTypeEnum,
  PinwheelCardInput,
  PinwheelExperienceInput,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import BillManagerErrorScreen from 'features/billManager/components/BillManagerErrorScreen/BillManagerErrorScreen';
import { logErrorEvent } from 'features/billManager/helpers/logErrorEvent';
import { serializeQueryError } from 'features/billManager/helpers/serializeQueryError';
import UnfreezeCardsBottomSheet from 'features/billSwitch/components/UnfreezeCardsBottomSheet/UnfreezeCardsBottomSheet';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import BillManagerRouteQuery from 'services/graphql/queries/BillManager/BillManagerRouteQuery';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { computeBillsTotal } from 'shared/helpers/computeBillsTotal/computeBillsTotal';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { useFetchCardsWithLockStatus } from 'shared/hooks/useFetchCardsWithLockStatus/useFetchCardsWithLockStatus';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';
import { useQuery } from 'urql';

const PINWHEEL_FLOW_ROUTE = '/bill-manager/pinwheel-flow';
const SOURCE_NAME = 'bill_manager_router';
const BUTTON_NAME = 'retry';
const ERROR_NAME = 'bill_manager_error';

const BillManagerRouterScreen = () => {
  const router = useRouter();
  const { t } = useTranslation('billManager');
  const [hasError, setHasError] = useState(false);
  const [isPinwheelFlow, setIsPinwheelFlow] = useState(false);
  const { segmentClient } = useAnalyticsConfiguration();
  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const hasPinwheelLaunchedRef = useRef(false);

  useBackHandler(navigateBack);

  const [
    { data, error: routeError, fetching: routeFetching },
    reExecuteRouteQuery,
  ] = useQuery({
    query: BillManagerRouteQuery,
    requestPolicy: 'network-only',
  });

  const {
    cards,
    hasCards,
    hasLockedCards,
    cardsError,
    cardsFetching,
    refetchCards,
  } = useFetchCardsWithLockStatus({ pause: !isPinwheelFlow });

  const hasActiveBelieveCard = cards?.some(
    (card) => card.type === CardTypeEnum.ChargeCard,
  );

  const cardOptions = useMemo(
    () => [
      PinwheelCardInput.Debit,
      ...(hasActiveBelieveCard ? [PinwheelCardInput.Believe] : []),
    ],
    [hasActiveBelieveCard],
  );

  const canLaunchPinwheel = hasCards && !hasLockedCards;

  const retrySucceeded =
    hasError && !routeError && !cardsError && !routeFetching && !cardsFetching;
  const showError = hasError && !retrySucceeded;

  const launchPinwheel = useCallback(() => {
    initiatePinwheel({
      input: {
        experience: PinwheelExperienceInput.BillManager,
        cardOptions,
      },
      sourceName: SOURCE_NAME,
      onLinkTokenError: (error) => {
        const detail = serializeQueryError(error);
        logBreadcrumb({
          level: 'error',
          message: 'Bill Manager link token error',
          data: detail,
        });
        logErrorEvent({
          segmentClient,
          error_type: 'link_token_error',
          error_step: 'pinwheel_launch',
          error_detail: detail,
        });
        setHasError(true);
      },
      onOutcome: ({ sessionState }) => {
        const { billSwitches } = sessionState ?? {};
        if (billSwitches?.length) {
          const amount = computeBillsTotal(billSwitches);
          router.replace({
            pathname: '/bill-switch/funding',
            params: { amt: amount },
          });
        } else {
          navigateBack();
        }
      },
    });
  }, [cardOptions, initiatePinwheel, segmentClient, setHasError, router]);

  useEffect(() => {
    if (data?.billManagerRoute?.route) {
      const route = data.billManagerRoute.route;
      if (route !== PINWHEEL_FLOW_ROUTE) {
        // dismissTo prevents multiple deep link clicks
        // from stacking multiple instances of the same screen.
        router.dismissTo(route);
      } else {
        setIsPinwheelFlow(true);
      }
    }
  }, [data, router]);

  // Once both queries have settled with no error, the retry succeeded.
  useEffect(() => {
    if (retrySucceeded) {
      setHasError(false);
    }
  }, [retrySucceeded]);

  useEffect(() => {
    if (isPinwheelFlow && !cardsFetching && !hasPinwheelLaunchedRef.current) {
      if (hasLockedCards) {
        hasPinwheelLaunchedRef.current = true;
        bottomSheetRef.current?.present();
      } else if (canLaunchPinwheel) {
        hasPinwheelLaunchedRef.current = true;
        launchPinwheel();
      }
    }
  }, [
    isPinwheelFlow,
    cardsFetching,
    canLaunchPinwheel,
    hasLockedCards,
    launchPinwheel,
  ]);

  useEffect(() => {
    if (!routeError || hasError) {
      return;
    }
    const detail = {
      ...serializeQueryError(routeError),
      had_route_data: Boolean(data?.billManagerRoute?.route),
      is_pinwheel_flow: isPinwheelFlow,
    };
    logBreadcrumb({
      level: 'error',
      message: 'Bill Manager route error',
      data: detail,
    });
    logErrorEvent({
      segmentClient,
      error_type: 'route_error',
      error_step: 'bill_manager_router',
      error_detail: detail,
    });
    setHasError(true);
  }, [routeError, segmentClient, data, isPinwheelFlow, hasError]);

  useEffect(() => {
    if (!cardsError || hasError) {
      return;
    }
    const detail = {
      ...serializeQueryError(cardsError),
      cards_fetching: cardsFetching,
      is_pinwheel_flow: isPinwheelFlow,
    };
    logBreadcrumb({
      level: 'error',
      message: 'Bill Manager cards error',
      data: detail,
    });
    logErrorEvent({
      segmentClient,
      error_type: 'fetch_cards_error',
      error_step: 'bill_manager_router',
      error_detail: detail,
    });
    setHasError(true);
  }, [cardsError, segmentClient, cardsFetching, isPinwheelFlow, hasError]);

  const handleRetry = useCallback(() => {
    reExecuteRouteQuery({ requestPolicy: 'network-only' });
    refetchCards();
  }, [reExecuteRouteQuery, refetchCards]);

  return (
    <>
      {isPinwheelFlow && (
        <UnfreezeCardsBottomSheet
          ref={bottomSheetRef}
          useBelieveLanguage={false}
          hasLockedCards={hasLockedCards}
          hasCards={hasCards}
          cards={cards}
          analyticsMetadata={{ source_name: SOURCE_NAME }}
          onSuccess={() => {
            bottomSheetRef.current?.dismiss();
            hasPinwheelLaunchedRef.current = false;
            launchPinwheel();
          }}
          onDismiss={() => {
            bottomSheetRef.current?.dismiss();
            navigateBack();
          }}
        />
      )}
      <ScreenTransitionOverlay
        isLoading={
          !showError || routeFetching || cardsFetching || isPinwheelLoading
        }
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={
          showError ? (
            <BillManagerErrorScreen
              messageHeadline={t('router.error.title')}
              messageBody={t('router.error.body')}
              primaryButton={{
                title: t('router.error.retryButton'),
                onPress: handleRetry,
                analytics: {
                  analyticsId: 'billManagerErrorRetryClicked',
                  customAttributes: {
                    button_name: BUTTON_NAME,
                    source_name: SOURCE_NAME,
                    error_name: ERROR_NAME,
                  },
                },
              }}
              secondaryButton={{
                title: t('router.error.backButton'),
                onPress: navigateBack,
              }}
            />
          ) : null
        }
      />
    </>
  );
};

export default BillManagerRouterScreen;
